import { toast } from "react-toastify";
//@ts-ignore
import { AxiosInstance, API_ROOT } from "@careinfo/axios-service";
import { accessToken } from "../helpers/sessionKey";
import print from "print-js";
import { useEffect, useState } from "react";

let instance: any;

// Initialize Axios instance
const initInstance = async () => {
  if (!instance) {
    instance = await AxiosInstance();
  }
};

const usePDFPrintHook = () => {
  const [isPrintLoading, setIsPrintLoading] = useState(false);

  const handleBeforeUnload = (event) => {
    event.preventDefault();
    event.returnValue =
      "Download in progress. Are you sure you want to leave the page?";
  };

  useEffect(() => {
    if (isPrintLoading) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    } else {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isPrintLoading]);

  const downloadPrint = async (downloadPath: string, formData?: any) => {
    await initInstance(); // Ensure Axios instance is initialized
    setIsPrintLoading(true);
    const toastId = toast("Starting to Print...", {
      type: "info",
      autoClose: false,
      draggable: false,
      closeButton: false,
      progress: 0,
    });

    try {
      // Initial POST request to start report generation, expecting JSON response
      const response = await instance({
        method: "POST",
        url: `${downloadPath}`,
        data: formData,
        headers: {
          Authorization: `JWT ${accessToken()}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status !== 200) {
        throw new Error("Failed to start the report generation");
      }

      toast.update(toastId, {
        render: "Printing...",
        progress: 0.1,
      });

      let progressValue = 0.1;
      const runProgressBar = () => {
        progressValue += 0.01;
        if (progressValue >= 0.9) progressValue = 0.9;
        toast.update(toastId, {
          progress: progressValue,
          autoClose: false,
        });
      };
      const progressInterval = setInterval(runProgressBar, 100);

      const pollAsyncUrl = `/download/api/download-history/check-for-download/?task_id=${response.data.task_id}`;

      const pollResults = async () => {
        try {
          // Polling with a GET request to check the download status
          const asyncResponse = await instance({
            method: "GET",
            url: pollAsyncUrl,
            headers: {
              Authorization: `JWT ${accessToken()}`,
            },
            responseType: "blob", // Blob response for the file after completion
            validateStatus: (status) => {
              // Handle both JSON (202) and Blob (200) responses
              return (status >= 200 && status < 300) || status === 202;
            },
          });

          if (asyncResponse.status === 202) {
            // If status is 202, keep polling
            setTimeout(pollResults, 750);
            setIsPrintLoading(true);
          } else if (asyncResponse.status === 200) {
            // When the status is 200, the blob response is ready
            clearInterval(progressInterval);
            toast.update(toastId, {
              render: "Report generation completed...",
              progress: 0.66,
            });

            setIsPrintLoading(false);
            const fileURL = window.URL.createObjectURL(asyncResponse.data);
            print(fileURL); // Print or download the file
            toast.update(toastId, {
              render: "Print complete!",
              type: "success",
              progress: 1,
            });
          }
        } catch (error) {
          setIsPrintLoading(false);
          clearInterval(progressInterval);
          console.error(error);
          toast.update(toastId, {
            render: "An error occurred while Printing.",
            type: "error",
            progress: 0,
            closeButton: true,
          });
        }
      };

      // Start polling
      pollResults();
    } catch (error) {
      setIsPrintLoading(false);
      console.error(error);
      toast.update(toastId, {
        render: "An error occurred while Printing.",
        type: "error",
        progress: 0,
        closeButton: true,
      });
    }
  };

  return { downloadPrint, isPrintLoading };
};

export default usePDFPrintHook;
